import { BrowserAuthError, Configuration, PublicClientApplication } from '@azure/msal-browser'
import { eqxAppConfigAuth } from '../interfaces/eqxAppConfig'
import eqxAuth from './eqxAuth'
import eqxEventBroker from './eqxEventBroker'

function isMsalStaleRedirectCacheError(err: unknown): boolean {
    if (!(err instanceof BrowserAuthError)) return false
    return (
        err.errorCode === 'no_cached_authority_error' ||
        err.errorCode === 'no_token_request_cache_error' ||
        err.errorCode === 'unable_to_parse_token_request_cache_error'
    )
}

interface eqxAppConfigAuthMsal extends eqxAppConfigAuth {
    auth: {
        clientId: string
        authority: string
        redirectUri: string
        postLogoutRedirectUri?: string
        navigateToLoginRequestUrl?: boolean
        scopes: string[]
    }
    cache?: {
        cacheLocation: string
        storeAuthStateInCookie: boolean
    }
}
class eqxAuthMsal extends eqxAuth {
    config: eqxAppConfigAuthMsal
    events: eqxEventBroker
    msalInstance: PublicClientApplication | null = null

    constructor(config: eqxAppConfigAuthMsal) {
        super(config)
        this.config = config //this just for typescript validator
        this.events = new eqxEventBroker(this)
        this.msalInstance = this.init()
    }
    init() {
        if (!this.config.auth) throw new Error('No auth config provided')
        this.events.trigger('init', [this.config])
        const { clientId, authority, redirectUri, postLogoutRedirectUri, navigateToLoginRequestUrl } =
            this.config.auth
        const cache = this.config.cache
        const msalConfig: Configuration = {
            auth: {
                clientId,
                authority,
                redirectUri,
                postLogoutRedirectUri: postLogoutRedirectUri ?? redirectUri,
                navigateToLoginRequestUrl: navigateToLoginRequestUrl ?? false,
                knownAuthorities: ['login.microsoftonline.com'],
            },
            cache: cache
                ? {
                      cacheLocation: cache.cacheLocation as 'localStorage' | 'sessionStorage',
                      storeAuthStateInCookie: cache.storeAuthStateInCookie,
                  }
                : undefined,
        }
        return new PublicClientApplication(msalConfig)
    }
    getMsalInstance() {
        if (!this.msalInstance) throw new Error('Msal instance not initialized')
        return this.msalInstance
    }
    isAuthenticated() {
        const accounts = this.getMsalInstance().getAllAccounts()
        return accounts.length > 0
    }
    async login() {
        this.events.trigger('loginStart')
        // Clear stale redirect interaction left by interrupted logout/navigation.
        await this.handleRedirectPromise()
        await this.getMsalInstance().loginRedirect({
            scopes: this.config.auth.scopes,
            authority: this.config.auth.authority,
        })
    }
    logout() {
        this.events.trigger('logoutStart')
        const instance = this.getMsalInstance()
        const accounts = instance.getAllAccounts()
        // logoutRedirect navigates away; do not call window.location.assign in parallel.
        return instance.logoutRedirect({
            account: accounts[0] ?? undefined,
            postLogoutRedirectUri: this.config.auth.postLogoutRedirectUri || this.config.auth.redirectUri,
        })
    }

    handleRedirectPromise() {
        return this.getMsalInstance()
            .handleRedirectPromise()
            .then((response) => {
                if (response !== null) {
                    // Login was successful
                    this.events.trigger('loginSuccess', [response.accessToken])
                    return response
                } else {
                    // No redirect response, user might have cancelled or there was an error
                    return null
                }
            })
            .catch((val) => {
                if (isMsalStaleRedirectCacheError(val)) {
                    console.warn('[MSAL] Ignoring stale redirect cache after interrupted logout:', val)
                    return null
                }
                this.events.trigger('logoutError', [val])
                throw val
            })
    }
    getAccessToken(): Promise<string | null> {
        const accounts = this.getMsalInstance().getAllAccounts()
        if (!accounts.length) return Promise.resolve(null)

        const account = accounts[0]
        this.events.trigger('getAccesTokenStart')
        return this.getMsalInstance()
            .acquireTokenSilent({
                scopes: this.config.auth.scopes,
                account: account,
                authority: this.config.auth.authority,
            })
            .then((response) => {
                console.log('Got token on AuthProvider', response)
                this.events.trigger('getAccessTokenSuccess', [response.accessToken])
                return response.accessToken
            })
            .catch((e) => {
                console.log('Error getting token on AuthProvider', e)
                this.events.trigger('getAccessTokenError', [e])
                return null
            })
    }
}
export { eqxAuthMsal as default, type eqxAppConfigAuthMsal }

